import { useEffect, useRef, useState } from "react";
import { buildMediaUrl } from "../../../utils/mediaType";

type QuestionAudioProps = {
  media: string;
  retryKey?: number;
  onError?: () => void;
  onLoad?: () => void;
};

const QuestionAudio = ({ media, retryKey = 0, onError, onLoad }: QuestionAudioProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.load();
    }
  }, [media, retryKey]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current) return;

    const progressBar = e.currentTarget;
    const clickPosition = e.nativeEvent.offsetX;
    const progressBarWidth = progressBar.offsetWidth;
    const newTime = (clickPosition / progressBarWidth) * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
    onLoad?.();
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  const progress = duration > 0 ? Math.min((currentTime / duration) * 100, 100) : 0;

  return (
    <div className="2xl:mt-[0.8vw] md:mt-3 mt-1 w-full 2xl:max-w-[32.083vw] max-w-[616px] mx-auto flex items-center 2xl:gap-[0.6vw] sm:gap-2 gap-1 justify-center">
      <audio
        ref={audioRef}
        src={buildMediaUrl(media, retryKey)}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onError={onError}
        preload="metadata"
      />

      <button
        onClick={togglePlay}
        className="skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[#F2BF7D] flex items-center 2xl:p-[0.5vw] md:p-2 p-1.5 landscape:max-lg:p-[7px] 2xl:rounded-[0.26vw] rounded-[5px] 2xl:text-[1.042vw]/tight xl:text-xl/tight text-base/tight font-bold text-white cursor-pointer justify-center text-nowrap 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] aspect-square"
      >
        {isPlaying ? (
          <svg
            className="2xl:w-[1.042vw] w-4 2xl:h-[1.042vw] h-4 landscape:max-lg:w-3.5 landscape:max-lg:h-3.5"
            width="26"
            height="33"
            viewBox="0 0 26 33"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.4062 3.46875V28.9062C10.4062 29.8262 10.0408 30.7085 9.39028 31.359C8.73976 32.0095 7.85747 32.375 6.9375 32.375H3.46875C2.54878 32.375 1.66649 32.0095 1.01597 31.359C0.365457 30.7085 0 29.8262 0 28.9062V3.46875C0 2.54878 0.365457 1.66649 1.01597 1.01597C1.66649 0.365457 2.54878 0 3.46875 0H6.9375C7.85747 0 8.73976 0.365457 9.39028 1.01597C10.0408 1.66649 10.4062 2.54878 10.4062 3.46875ZM21.9688 0H18.5C17.58 0 16.6977 0.365457 16.0472 1.01597C15.3967 1.66649 15.0312 2.54878 15.0312 3.46875V28.9062C15.0312 29.8262 15.3967 30.7085 16.0472 31.359C16.6977 32.0095 17.58 32.375 18.5 32.375H21.9688C22.8887 32.375 23.771 32.0095 24.4215 31.359C25.072 30.7085 25.4375 29.8262 25.4375 28.9062V3.46875C25.4375 2.54878 25.072 1.66649 24.4215 1.01597C23.771 0.365457 22.8887 0 21.9688 0Z"
              fill="#110706"
            />
          </svg>
        ) : (
          <svg
            className="2xl:w-[1.042vw] w-4 2xl:h-[1.042vw] h-4 landscape:max-lg:w-3.5 landscape:max-lg:h-3.5"
            width="24"
            height="28"
            viewBox="0 0 24 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22.5 11.4019C24.5 12.5566 24.5 15.4434 22.5 16.5981L4.5 26.9904C2.5 28.1451 0 26.7017 0 24.3923L0 3.60769C0 1.29829 2.5 -0.145073 4.5 1.00962L22.5 11.4019Z"
              fill="#110706"
            />
          </svg>
        )}
      </button>
      <div
        className="2xl:gap-[0.2vw] sm:gap-2 gap-1 w-full 2xl:max-w-[17.656vw] max-w-[339px] 
                            skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[#F2BF7D] flex items-center 2xl:py-[0.4vw] lg:py-1.5 py-1 landscape:max-lg:py-1 2xl:px-[0.7vw] px-3 2xl:rounded-[0.26vw] rounded-[5px] 2xl:text-[1.042vw]/tight xl:text-xl/tight text-base/tight font-bold text-white cursor-pointer justify-center text-nowrap 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
      >
        <span className="text-black-300 font-bold 2xl:text-[1.042vw]/tight text-base/tight max-[374px]:text-sm/tight 2xl:min-w-[2.969vw] md:min-w-[57px] min-w-[45px] landscape:max-lg:min-w-9 max-[374px]:min-w-[40px]">
          {formatTime(currentTime)}
        </span>

        <div
          className="flex-1 2xl:h-[0.521vw] h-2.5 bg-[#E7E6E6] rounded-full cursor-pointer relative overflow-hidden 2xl:px-[0.156vw] px-[3px]"
          onClick={handleProgressClick}
        >
          <div className="2xl:h-[0.313vw] h-1.5 rounded-full bg-[#D0D0D0] w-full absolute top-1/2 -translate-y-1/2" />
          <div
            className="2xl:h-[0.313vw] h-1.5 rounded-full bg-[#8B7355] absolute top-1/2 -translate-y-1/2 transition-all duration-100 z-10"
            style={{
              width: `${progress}%`,
            }}
          />
        </div>

        <span className="text-end text-black-300 font-bold 2xl:text-[1.042vw]/tight text-base/tight max-[374px]:text-sm/tight 2xl:min-w-[2.969vw] md:min-w-[57px] min-w-[45px] landscape:max-lg:min-w-9  max-[374px]:min-w-[40px]">
          {formatTime(duration)}
        </span>
      </div>
    </div>
  );
};

export default QuestionAudio;
